/**
 * decide_clause_proposal — structural agent tool. Wraps the proposal
 * accept/reject endpoint with a preview/confirm cycle so the user
 * sees what's being decided before it's persisted.
 *
 * Accepted proposals get synthesized into `controlled_by` edges on
 * the next FederatedGraphService.buildView and flip the clause to
 * addressed. Rejected proposals stay in the audit trail as
 * 'rejected' status but no longer bind anything.
 */

import type { IAgentTool, IAgentToolContext, AgentToolResult } from '../../../../core/ai/agent/IAgentTool';

function getFolderId(context: IAgentToolContext): string | null {
  const ctx = context.assistantContext as Record<string, unknown> | undefined;
  const f = ctx?.folderId;
  return typeof f === 'string' && f.length > 0 ? f : null;
}

type Decision = 'accepted' | 'rejected';

export const decideClauseProposalTool: IAgentTool = {
  name: 'decide_clause_proposal',
  description:
    'Accept or reject one AI-suggested clause↔process mapping (proposal). Accept binds the process step as evidence for the clause; reject keeps the audit trail but removes the suggestion from the queue. Returns a preview the user must confirm.',
  parameters: {
    type: 'object',
    properties: {
      proposalId: {
        type: 'string',
        description: 'UUID of the proposal (from review_clause output).',
      },
      decision: {
        type: 'string',
        description: '"accepted" or "rejected".',
      },
      reviewerNotes: {
        type: 'string',
        description: 'Optional note recorded with the decision for audit trail.',
      },
    },
    required: ['proposalId', 'decision'],
  },
  supportedDiagramTypes: [],
  tier: 'structural',
  category: 'edit',

  async execute(params, context): Promise<AgentToolResult> {
    const folderId = getFolderId(context);
    if (!folderId) return { success: false, summary: 'No folder context available.' };
    const proposalId = String(params.proposalId ?? '');
    const decision = String(params.decision ?? '') as Decision;
    const reviewerNotes =
      typeof params.reviewerNotes === 'string' ? params.reviewerNotes : undefined;
    if (!proposalId) return { success: false, summary: 'proposalId is required.' };
    if (decision !== 'accepted' && decision !== 'rejected') {
      return { success: false, summary: 'decision must be "accepted" or "rejected".' };
    }

    const url = `/api/compass/proposals/${encodeURIComponent(proposalId)}/decide`;
    const action = decision === 'accepted' ? 'Accept' : 'Reject';

    return {
      success: true,
      summary: `Ready to ${action.toLowerCase()} proposal ${proposalId.slice(0, 8)}. Confirm to persist.`,
      uiSurface: 'chat',
      preview: {
        description:
          `${action} AI proposal ${proposalId.slice(0, 8)}` +
          (reviewerNotes ? `\n\nReviewer note: ${reviewerNotes}` : ''),
        delta: { additions: [], removals: [], moves: [], updates: [] },
        confirm: {
          kind: 'external-effect',
          run: async () => {
            const r = await fetch(url, {
              method: 'POST',
              headers: { 'content-type': 'application/json' },
              body: JSON.stringify({ decision, reviewerNotes }),
            });
            if (!r.ok) throw new Error(`Decision failed (${r.status})`);
          },
        },
      },
    };
  },
};
